import { Component, OnInit, ViewChild } from '@angular/core';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { Store } from '@ngxs/store';
import { EditorComponent } from '@progress/kendo-angular-editor';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ISpecificBrickSettingsComponent } from 'src/app/components/dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { EditorType } from 'src/app/samwebDescription/samweb/editorType';
import { PresentationType } from 'src/app/samwebDescription/samweb/settings/presentationType';
import { ConfigurableEditorSettingRemoteDto, SingleCallTagEditorBrickSettingRemoteDto } from '../../../../../../generated/swagger';
import {
   ChangeCallTagDisplayNameValueForSingleCallTagBrickSettings,
   ChangeCallTagIdForSingleCallTagBrickSettings,
   ChangeCallTagSourceActiveCallForSingleCallTagBrickSettings,
   ChangeCallTagSourceCallHistoryForSingleCallTagBrickSettings,
   ChangeClearTagValueForSingleCallTagBrickSettings,
   ChangeEditBackgroundColorForSingleCallTagBrickSettings,
   ChangeEditFontColorForSingleCallTagBrickSettings,
   ChangeIsReadOnlyForSingleCallTagBrickSettings,
   ChangeShowDescriptionTextAsPopoverForSingleCallTagBrickSettings,
   ChangeTagEditorDescriptionTextForSingleCallTagBrickSettings,
   ChangeTagEditorPresentationTypeForSingleCallTagBrickSettings,
   ChangeTagEditorSettingsForSingleCallTagBrickSettings,
   ChangeTagEditorTypeForSingleCallTagBrickSettings,
   ChangeTagIconColorForSingleCallTagBrickSettings,
   ChangeTagIconForSingleCallTagBrickSettings,
} from '../../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/single-call-tag-brick-settings/single-call-tag-brick-settings.actions';
import { SingleCallTagBrickSettingsState } from '../../../../../store/dashboard/brick-wall/brick-settings/specific-brick-settings/single-call-tag-brick-settings/single-call-tag-brick-settings.state';

@Component({
   selector: 'samweb-single-call-tag-editor-settings',
   templateUrl: './single-call-tag-editor-settings.component.html',
   styleUrls: ['./single-call-tag-editor-settings.component.scss'],
})
export class SingleCallTagEditorSettingsComponent implements OnInit, ISpecificBrickSettingsComponent {
   public settings$: Observable<SingleCallTagEditorBrickSettingRemoteDto>;
   public selectedEditorType$: Observable<EditorType>;
   public configurableEditorSettings$: Observable<ConfigurableEditorSettingRemoteDto>;
   public presentationType$: Observable<PresentationType>;

   public brickInstanceId: string;
   public presentationTypePossibleValues = PresentationType.values();

   public tagEditorDescription: string;

   @ViewChild(EditorComponent)
   public kendoEditor: EditorComponent;

   constructor(private store: Store) {}

   ngOnInit() {
      this.settings$ = this.store.select(SingleCallTagBrickSettingsState.getSettings).pipe(map((getEditorSettings) => getEditorSettings(this.brickInstanceId)));
      this.tagEditorDescription = this.store.selectSnapshot(SingleCallTagBrickSettingsState.getDescriptionText)(this.brickInstanceId);
      this.selectedEditorType$ = this.store.select(SingleCallTagBrickSettingsState.getEditorType).pipe(map((fn) => fn(this.brickInstanceId)));
      this.configurableEditorSettings$ = this.settings$.pipe(map((x) => x.editorSettings));
      this.presentationType$ = this.store.select(SingleCallTagBrickSettingsState.getTagPresentationType).pipe(map((fn) => fn(this.brickInstanceId)));
   }

   callTagIdChanged(callTagId: string) {
      this.store.dispatch(new ChangeCallTagIdForSingleCallTagBrickSettings(this.brickInstanceId, callTagId));
   }

   iconChanged(icon: IconDefinition) {
      this.store.dispatch(new ChangeTagIconForSingleCallTagBrickSettings(this.brickInstanceId, icon));
   }

   iconColorChanged(color: string) {
      this.store.dispatch(new ChangeTagIconColorForSingleCallTagBrickSettings(this.brickInstanceId, color));
   }

   clearIconColor() {
      this.store.dispatch(new ChangeTagIconColorForSingleCallTagBrickSettings(this.brickInstanceId, null));
   }

   fontColorChanged(color: string) {
      this.store.dispatch(new ChangeEditFontColorForSingleCallTagBrickSettings(this.brickInstanceId, color));
   }

   clearFontColor() {
      this.store.dispatch(new ChangeEditFontColorForSingleCallTagBrickSettings(this.brickInstanceId, null));
   }

   backgroundColorChanged(color: string) {
      this.store.dispatch(new ChangeEditBackgroundColorForSingleCallTagBrickSettings(this.brickInstanceId, color));
   }

   clearBackgroundColor() {
      this.store.dispatch(new ChangeEditBackgroundColorForSingleCallTagBrickSettings(this.brickInstanceId, null));
   }

   isReadOnlyChanged(value: boolean) {
      this.store.dispatch(new ChangeIsReadOnlyForSingleCallTagBrickSettings(this.brickInstanceId, value));
   }

   callTagTypeChanged(value: EditorType) {
      this.store.dispatch(new ChangeTagEditorTypeForSingleCallTagBrickSettings(this.brickInstanceId, value));
   }

   editorSettingsChanged(editorSettings: ConfigurableEditorSettingRemoteDto) {
      this.store.dispatch(new ChangeTagEditorSettingsForSingleCallTagBrickSettings(this.brickInstanceId, editorSettings));
   }

   presentationTypeChangedChanged(presentationType: PresentationType) {
      this.store.dispatch(new ChangeTagEditorPresentationTypeForSingleCallTagBrickSettings(this.brickInstanceId, presentationType));
   }

   tagEditorDescriptionChanged(descriptionText: string) {
      this.store.dispatch(new ChangeTagEditorDescriptionTextForSingleCallTagBrickSettings(this.brickInstanceId, descriptionText));
   }

   showDescriptionTextAsPopoverChanged(showDescriptionTextAsPopover: boolean) {
      this.store.dispatch(new ChangeShowDescriptionTextAsPopoverForSingleCallTagBrickSettings(this.brickInstanceId, showDescriptionTextAsPopover));
   }

   clearTagValueChanged(clearTagValue: string) {
      this.store.dispatch(new ChangeClearTagValueForSingleCallTagBrickSettings(this.brickInstanceId, clearTagValue));
   }

   callTagDisplayNameChanged(displayName: string) {
      this.store.dispatch(new ChangeCallTagDisplayNameValueForSingleCallTagBrickSettings(this.brickInstanceId, displayName));
   }

   showCallTagsForActiveCallChanged(isEnabled: boolean) {
      this.store.dispatch(new ChangeCallTagSourceActiveCallForSingleCallTagBrickSettings(this.brickInstanceId, isEnabled));
   }

   showCallTagsForHistoryCallsChanged(isEnabled: boolean) {
      this.store.dispatch(new ChangeCallTagSourceCallHistoryForSingleCallTagBrickSettings(this.brickInstanceId, isEnabled));
   }
}
